import { useMemo, useState } from 'react'
import { useStories } from '@/hooks/useHeadlines'
import { useYourList, useListStats, useIssues, listMissing } from '@/hooks/useNewsletter'
import { useNewsletterItems } from '@/hooks/useNewsletterItems'
import { useAuthStore } from '@/store/auth'
import { supabase } from '@/lib/supabase'
import { ROUTES } from '@/components/gg/chrome'

/**
 * The newsletter on the phone (Brian's after/nl-phone.html v1, Oct 21: "have
 * the newsletter tab on there … it's best displayed on a desktop device, just
 * so they know they have the feature … some people may never log into a
 * computer"). Read only: the feature is theirs, best on a computer, this
 * week's issue so far, whether it is set up to send, and Email me a link.
 * Building, sponsors, the brand studio and Your list stay on the computer.
 *
 * "This week's issue so far" lists the stories saved with ＋ Newsletter
 * (newsletter_items); before any, the newest unsent issue's stories.
 */
export function NewsletterPhone() {
  const { data: stories } = useStories()
  const { data: issues } = useIssues()
  const { data: yourList } = useYourList()
  const { data: stats } = useListStats()
  const { items } = useNewsletterItems()
  const email = useAuthStore((s) => s.user?.email ?? null)
  const [toast, setToast] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const list = useMemo(() => {
    if (items.length) return items.map((i) => ({ id: i.id, title: i.title, src: i.source_name }))
    const draft = issues?.find((i) => i.status === 'draft' || i.status === 'failed')
    const ids = (draft?.config?.storyIds ?? []) as string[]
    return ids.map((id) => stories?.find((s) => s.id === id)).filter(Boolean)
      .map((s) => ({ id: s!.id, title: s!.headline, src: s!.source_name }))
  }, [issues, stories, items])
  const connected = !!yourList && !listMissing(yourList, stats?.subscribed ?? 0)

  async function emailLink() {
    if (!email) { setToast({ ok: false, text: 'There’s no email on your account to send it to.' }); return }
    setBusy(true)
    // A sign-in link to the account's own address that opens the newsletter.
    const { error } = await supabase.auth.signInWithOtp({
      email, options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}${ROUTES.newsletter}` },
    })
    setBusy(false)
    setToast(error
      ? { ok: false, text: error.message }
      : { ok: true, text: 'Sent. Open the email on your computer to go straight to your newsletter.' })
  }

  return (
    <div className="nl-phone">
      <div className="nl-phone__eb">Part of your GoGrov</div>
      <h1 className="nl-phone__h">Your weekly newsletter</h1>
      <p className="nl-phone__lead">A newsletter in your name, from the stories you pick. Your readers get it in their inbox every week.</p>
      <section className="nl-phone__desk">
        <svg className="nl-phone__lap" viewBox="0 0 58 42" aria-hidden="true"><rect x="9" y="4" width="40" height="27" rx="2.5" /><path d="M3 35h52l-3.5 4h-45z" /><path d="M25 35.5h8" /></svg>
        <h2>Best on a computer</h2>
        <p>Laying out the issue, adding sponsors and sending it all work best on a bigger screen.</p>
        <ol>
          <li>On your computer, go to <b>gogrov.com</b> and sign in.</li>
          <li>Open <b>Newsletter</b>. The stories you saved here are waiting.</li>
        </ol>
      </section>
      <div className="nl-phone__sect"><span>This week’s issue so far</span><span>{list.length ? `${list.length} ${list.length === 1 ? 'story' : 'stories'}` : ''}</span></div>
      <div className="nl-phone__issue">
        {list.length
          ? list.map((x, i) => (
            <div key={x.id} className="nl-phone__it">
              <span className="nl-phone__n">{String(i + 1).padStart(2, '0')}</span>
              <div className="nl-phone__t">{x.title}{x.src && <small>{x.src}</small>}</div>
            </div>
          ))
          : <div className="nl-phone__none">Nothing saved yet. Tap <b>＋ Newsletter</b> on any story in your brief to save it for this week’s issue.</div>}
      </div>
      <div className={`nl-phone__status${connected ? ' is-on' : ''}`}>
        <i />{connected ? `Goes out ${yourList?.send_day || 'Thursday'} to ${(stats?.subscribed ?? 0).toLocaleString()} people once you send it from your computer` : 'Not set up to send yet. That’s done on your computer.'}
      </div>
      <button type="button" className="gg-btn nl-phone__go" disabled={busy} onClick={() => void emailLink()}>{busy ? 'Sending' : 'Email me a link'}</button>
      {toast && <p className={toast.ok ? 'gg-ok' : 'gg-error'} role="status">{toast.text}</p>}
    </div>
  )
}
